import type { UserRole } from '@shared/types';

/**
 * ロール階層。数値が大きいほど上位（広い権限）。
 * 上位ロールは下位ロールの権限をすべて内包する。
 */
export const ROLE_RANK: Record<UserRole, number> = {
  general: 1,
  accounting: 2,
  admin: 3,
  system_admin: 4,
};

/** 上位→下位の順に並んだ全ロール（UI選択肢の表示順に使用）。 */
export const ALL_ROLES: readonly UserRole[] = [
  'system_admin',
  'admin',
  'accounting',
  'general',
];

/** ロールの日本語ラベル（UI表示の単一の真実の源）。 */
export const ROLE_LABELS: Record<UserRole, string> = {
  system_admin: 'システム管理者',
  admin: '管理者',
  accounting: '経理',
  general: '一般',
};

/**
 * `role` が `minimum` 以上の権限を持つか（階層判定）。
 * 例: meetsRole('admin', 'accounting') === true
 */
export function meetsRole(
  role: UserRole | null | undefined,
  minimum: UserRole
): boolean {
  if (!role) return false;
  return ROLE_RANK[role] >= ROLE_RANK[minimum];
}

/** システム管理者か。 */
export function isSystemAdminRole(role: UserRole | null | undefined): boolean {
  return meetsRole(role, 'system_admin');
}

/** 管理者以上（管理者・システム管理者）か。 */
export function isAdminRole(role: UserRole | null | undefined): boolean {
  return meetsRole(role, 'admin');
}

/** 経理以上（経理・管理者・システム管理者）か。 */
export function isAccountingRole(role: UserRole | null | undefined): boolean {
  return meetsRole(role, 'accounting');
}

/**
 * 請求書を「発行(issued)」ステータスへ変更できるか。
 * 一般は下書き作成までで、発行への遷移は経理以上のみ。
 */
export function canIssueInvoice(role: UserRole | null | undefined): boolean {
  return isAccountingRole(role);
}

/** 与えられた値が有効な UserRole か（外部入力の検証用）。 */
export function isValidRole(value: unknown): value is UserRole {
  return typeof value === 'string' && value in ROLE_RANK;
}
